export type ContentItem =
  | { type: "section-heading"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list-good"; items: string[] }
  | { type: "list-bad"; items: string[] }
  | { type: "list-numbered"; items: string[] }
  | { type: "list-plain"; items: string[] }
  | { type: "callout-info"; title: string; text: string }
  | { type: "callout-warning"; title: string; text: string }
  | { type: "flowchart"; steps: FlowchartStep[] }
  | { type: "checklist-group"; title: string; items: string[] };

export interface FlowchartStep {
  number: string;
  question: string;
  yes: string;
  no: string;
}

export interface BookChapter {
  id: string;
  title: string;
  content: ContentItem[];
}

export const introContent = {
  foreword: [
    "私は、2026年以降の住宅購入について、条件が整っている人は先送りしすぎない方がよいと考えています。",
    "理由は明確です。",
    "インフレによって現金の価値は目減りし、建築費と人件費は高止まりし、賃料にも上昇圧力がかかっています。さらに、金利が上がれば、同じ年収でも借りられる金額は下がります。",
    "つまり、何もしないこともリスクです。",
    "ただし、ここで誤解してはいけません。私は「誰でも今すぐ家を買うべきだ」と言っているのではありません。",
    "買ってよい人と、まだ買ってはいけない人は明確に分かれます。",
    "買ってよいのは、家計に余力があり、金利上昇に耐えられ、将来売れる・貸せる物件を選べる人です。",
    "反対に、貯金を使い切る人、借入上限まで借りる人、出口の弱い物件を勢いで買う人は、今の時代でも買ってはいけません。",
    "住宅購入は、怖がって何もしないこともリスクです。しかし、焦って買うこともリスクです。",
    "大切なのは、買うか買わないかではなく、買ってよい条件が整っているかです。",
  ],
  conclusion: [
    "住宅購入で失敗する人の多くは、物件選びの前にすでに間違えています。",
    "借入可能額だけを見ている。月々の返済額だけを見て、固定資産税や修繕費を見ていない。駅から遠いけど広いからいい、と考えて将来の売却を見ていない。金利が上がったときの家計を計算していない。",
    "つまり、失敗する人は「買う前の見方」を間違えています。",
    "この資料では、買ってよい人・買ってはいけない人の判断基準、危ない物件・危ない営業トーク、住宅ローンの正しい組み方、そして出口戦略まで、橋本裕介の視点でまとめました。",
  ],
};

export const bookContent: BookChapter[] = [
  {
    id: "chapter-1",
    title: "第1章：2026年、住宅購入を先送りしすぎるリスク",
    content: [
      {
        type: "section-heading",
        text: "1.1 インフレと住宅購入の関係",
      },
      {
        type: "paragraph",
        text: "2026年の日本において、住宅購入を検討する人が直面している状況は、これまでとは異なります。長年続いたデフレ・低金利の時代が終わりを告げ、インフレと金利上昇が現実のものとなっています。",
      },
      {
        type: "paragraph",
        text: "インフレが続く環境では、現金の実質的な価値は目減りします。一方で、不動産は物価と連動して価値が維持・上昇する傾向があります。建築費・人件費の高騰は新築価格を押し上げ、既存の建物の相対的価値を高めています。",
      },
      {
        type: "paragraph",
        text: "ただし、これは「今すぐ家を買うべきだ」という意味ではありません。「条件が整っている人は先送りしすぎるな」という意味です。",
      },
      {
        type: "section-heading",
        text: "1.2 先送りのコスト",
      },
      {
        type: "paragraph",
        text: "住宅購入を先送りにすることには、見えにくいコストがあります。",
      },
      {
        type: "list-plain",
        items: [
          "建築費の高止まりにより、新築住宅の価格は下がりにくい状態が続いています。",
          "インフレが続けば、現在保有している現金の実質的な購買力は低下します。",
          "金利が上昇すると、同じ年収でも借りられる金額が減少します。",
          "賃料も上昇圧力がかかっており、賃貸住まいのコストも増加傾向にあります。",
        ],
      },
      {
        type: "section-heading",
        text: "1.3 買ってよい人・買ってはいけない人",
      },
      {
        type: "paragraph",
        text: "条件が整っている人とそうでない人を明確に分けることが重要です。",
      },
      {
        type: "callout-info",
        title: "買ってよい人の条件",
        text: "",
      },
      {
        type: "list-good",
        items: [
          "購入後も生活費6〜12か月分の現金（イメージ100万円以上）を残せる。",
          "金利が1〜2%上昇しても返済に余裕がある人。（毎月の支払いが2,3万増加に耐えられるか）",
          "少なくとも10年以上住む見込みがある。",
          "売れる・貸せる立地を選んでいる。",
          "感情ではなく数字で判断できる。",
          "家族の変化を想定している。",
        ],
      },
      {
        type: "paragraph",
        text: "そのうえでインフレに強い金融資産である住宅用不動産を選ぶという流れです。",
      },
      {
        type: "callout-warning",
        title: "買ってはいけない人の条件",
        text: "",
      },
      {
        type: "list-bad",
        items: [
          "頭金を使い切り、手元資金がほぼゼロになる人。",
          "借入可能額の上限まで借りようとしている人。",
          "金利上昇のリスクシミュレーションをしていない人。",
          "出口（売却・賃貸）の見込みが薄いエリア・物件を選ぼうとしている人。",
          "家族構成・収入の変化を想定していない人。",
        ],
      },
      {
        type: "section-heading",
        text: "1.4 購入判断のフローチャート",
      },
      {
        type: "paragraph",
        text: "住宅購入の判断は、以下のステップで確認してください。",
      },
      {
        type: "flowchart",
        steps: [
          {
            number: "1",
            question: "購入後も生活費6〜12か月分の現金（100万円以上）が残りますか？",
            yes: "次のステップへ進む",
            no: "まだ早い",
          },
          {
            number: "2",
            question: "金利が2%上がっても返済できますか？（月2,3万の支払い増加）",
            yes: "次のステップへ進む",
            no: "見直しが必要",
          },
          {
            number: "3",
            question: "10年後に売れる・貸せる物件ですか？",
            yes: "次のステップへ進む",
            no: "別の物件を探す",
          },
          {
            number: "4",
            question: "売れる・貸せる立地かどうか確認しましたか？（専門家に意見を求める）",
            yes: "次のステップへ進む",
            no: "タイミングを再考",
          },
          {
            number: "5",
            question: "修繕費・税金・保険まで含めて計算しましたか？（専門家に意見を求める）",
            yes: "次のステップへ進む",
            no: "物件・借入を見直す",
          },
          {
            number: "6",
            question: "家族構成・転職→介護→老後まで考えましたか？",
            yes: "住宅購入を検討できる状態",
            no: "ライフプランを再考",
          },
        ],
      },
      {
        type: "paragraph",
        text: "全てにYESであれば、住宅購入を検討できる状態です。一つでもNOがあれば、その課題を解決してから判断してください。",
      },
    ],
  },
  {
    id: "chapter-2",
    title: "第2章：インフレ時代に強い不動産・弱い不動産",
    content: [
      {
        type: "section-heading",
        text: "2.1 不動産とインフレの関係",
      },
      {
        type: "paragraph",
        text: "不動産は一般的に、インフレに対するヘッジ（防御）手段とされています。物価が上昇すると、建築費・人件費・土地価格も上昇するため、不動産の価格・賃料もともに上昇しやすく、建物の建て替えコスト上昇が既存不動産の相対的価値を下支えすることもあります。",
      },
      {
        type: "paragraph",
        text: "ただし、これは「どんな不動産でもインフレに強い」という意味ではありません。インフレに強い不動産と弱い不動産は明確に分かれます。",
      },
      {
        type: "section-heading",
        text: "2.2 持ち家が資産防衛になる条件",
      },
      {
        type: "paragraph",
        text: "立地とローンを間違えなければ、持ち家はインフレ時代の資産防衛になります。",
      },
      {
        type: "paragraph",
        text: "インフレ時代に強い不動産の条件は以下の通りです。",
      },
      {
        type: "list-good",
        items: [
          "駅から徒歩10分以内の利便性の高いエリアであること。",
          "人口が維持・増加しているエリアであること。",
          "賃貸需要が継続的にあること。",
          "物件の維持管理がしっかりされていること。",
          "買い過ぎずに金利上昇余地を持った借入額であること。",
        ],
      },
      {
        type: "paragraph",
        text: "これらの条件を満たす物件であれば、インフレ環境下で実質的な資産価値を保ちやすくなります。また、賃料相当分を払い続けるより、ローン返済で資産を積み上げる意義が生まれます。",
      },
      {
        type: "section-heading",
        text: "2.3 インフレでも負ける不動産",
      },
      {
        type: "paragraph",
        text: "一方で、インフレ環境でも資産価値が下がりやすい不動産があります。",
      },
      {
        type: "list-bad",
        items: [
          "駅から遠く、生活利便性が低いエリアの物件は、人口減少の影響を受けやすく、賃貸需要が細りやすいです。",
          "老朽化した建物で修繕積立が不足しているマンションは、将来の大規模修繕一時金リスクを抱えています。",
          "管理組合の機能が弱いマンションは、建物の劣化が早く、資産価値の維持が困難です。",
        ],
      },
      {
        type: "paragraph",
        text: "インフレだから不動産は上がる、という単純な話ではありません。買う物件と買い方によって、結果は大きく変わります。",
      },
      {
        type: "section-heading",
        text: "2.4 住宅購入はエリア選びでほぼ決まる",
      },
      {
        type: "paragraph",
        text: "住宅購入の成否の大部分は、物件を見る前に決まります。エリア選びです。",
      },
      {
        type: "paragraph",
        text: "エリアを選ぶ際に確認すべきことは以下の通りです。",
      },
      {
        type: "list-plain",
        items: [
          "その地域の人口推移と将来予測。",
          "主要駅へのアクセス。",
          "周辺の賃貸需要（空室率・賃料水準）。",
          "ハザードマップの確認。",
          "近隣の開発計画・廃止計画。",
        ],
      },
      {
        type: "paragraph",
        text: "エリアが決まれば、その中で最も条件のよい物件を選ぶことができます。しかし、エリアを間違えると、どれだけ物件自体が良くても出口が限られます。",
      },
      {
        type: "callout-info",
        title: "不動産はエリア選びで7割の勝負が決まります。",
        text: "",
      },
    ],
  },
  {
    id: "chapter-3",
    title: "第3章：建築費高騰は新築だけの話ではない",
    content: [
      {
        type: "section-heading",
        text: "3.1 建築費・人件費は高止まりしやすい",
      },
      {
        type: "paragraph",
        text: "建築費の上昇は、一時的な現象ではありません。",
      },
      {
        type: "paragraph",
        text: "鉄骨・コンクリート・木材などの建設資材は、原材料費・輸送コスト・為替の影響を受けて上昇しています。さらに、職人・施工管理者などの人件費も高止まりしています。これは需要と供給の構造的な問題であり、短期的に解消される見込みは低いと考えられます。",
      },
      {
        type: "paragraph",
        text: "こうした状況は、新築の価格に直接反映されています。都市部を中心に新築マンション・戸建ての価格水準は高止まりしており、数年前と比較して同じ予算で買える物件の質・広さ・立地が制約されています。",
      },
      {
        type: "section-heading",
        text: "3.2 省エネ基準・2024年問題・職人不足",
      },
      {
        type: "paragraph",
        text: "建築コスト上昇を加速させている要因が、省エネ基準の強化と2024年問題です。",
      },
      {
        type: "paragraph",
        text: "省エネ基準の義務化により、断熱材・窓・設備など、建物性能に関わるコストが増加しています。これは今後も続く方向性です。",
      },
      {
        type: "paragraph",
        text: "2024年問題は、建設業における時間外労働規制の適用です。労働時間の上限規制により、施工期間が延びたり、工事費が増加したりする影響が出ています。現場の職人不足と相まって、施工体制の逼迫が続いています。",
      },
      {
        type: "paragraph",
        text: "これらの構造的要因は、建築費の高止まりを長期化させる可能性があります。",
      },
      {
        type: "section-heading",
        text: "3.3 中古住宅にも修繕費高騰が波及する",
      },
      {
        type: "paragraph",
        text: "「建築費が高いなら中古でいい」という判断は、一定の合理性があります。しかし注意点があります。",
      },
      {
        type: "paragraph",
        text: "中古住宅の修繕・リフォームコストも、建築費と同じく上昇しています。外壁塗装・屋根修繕・設備交換・耐震補強など、住宅の維持に必要な工事費が軒並み上がっています。",
      },
      {
        type: "paragraph",
        text: "また、マンションの場合、大規模修繕工事費の増加により、修繕積立金の不足が顕在化しているケースが増えています。購入前に修繕積立金の現状と計画を確認しないと、購入後に一時金の負担を求められるリスクがあります。",
      },
      {
        type: "section-heading",
        text: "3.4 「中古なら安い」はもう通用しにくい",
      },
      {
        type: "paragraph",
        text: "中古住宅の価格も、新築価格の上昇に引っ張られる形で上昇しています。特に都市部・利便性の高いエリアでは、築10〜20年の中古でも高値がついているケースがあります。",
      },
      {
        type: "paragraph",
        text: "中古を選ぶ場合には、以下の点を必ず確認してください。",
      },
      {
        type: "list-plain",
        items: [
          "築年数と耐震基準（1981年以前は旧耐震、2000年以前も要注意）",
          "修繕積立金の残高・不足状況（マンションの場合）",
          "リフォーム歴と修繕の内容",
          "ホームインスペクションの実施有無",
        ],
      },
      {
        type: "paragraph",
        text: "中古には中古のリスクがあります。価格だけで判断せず、実質コスト全体で比較することが重要です。",
      },
    ],
  },
  {
    id: "chapter-4",
    title: "第4章：賃貸は自由、持ち家は資産防衛",
    content: [
      {
        type: "section-heading",
        text: "4.1 賃貸のメリットは「人生の選択肢を残すこと」",
      },
      {
        type: "paragraph",
        text: "賃貸の最大のメリットは、人生の変化に対応できる柔軟性です。",
      },
      {
        type: "paragraph",
        text: "転勤・転職・結婚・離婚・家族構成の変化・介護など、人生では想定外の変化が起きます。賃貸であれば、住む場所を変えることで、こうした変化に対応できます。持ち家だと、売却・賃貸化・引越しのコストと手間が発生します。",
      },
      {
        type: "paragraph",
        text: "また、住宅ローンを抱えていない状態は、収入の変動リスクに対して強いです。失業・病気・収入減少があっても、賃貸なら家賃を下げる選択肢があります。",
      },
      {
        type: "section-heading",
        text: "4.2 持ち家のメリットは「インフレへの対応と資産化」",
      },
      {
        type: "paragraph",
        text: "持ち家の本質的なメリットは、インフレ時代における資産防衛です。",
      },
      {
        type: "paragraph",
        text: "インフレが進むと、現金の価値は下がり、実物資産である不動産の価値は相対的に維持されやすくなります。住宅ローンも固定金利で借りていれば、返済額は変わらないまま、物件価値と賃料相場が上昇する可能性があります。",
      },
      {
        type: "paragraph",
        text: "また、住宅ローン完済後は「住居費ゼロ（管理費・固定資産税を除く）」の状態になります。老後の生活コストを大幅に下げる効果があります。",
      },
      {
        type: "section-heading",
        text: "4.3 賃貸か持ち家かの判断基準",
      },
      {
        type: "paragraph",
        text: "この二択に「絶対的な答え」はありません。個人の状況・価値観・エリア・家族構成によって変わります。",
      },
      {
        type: "callout-info",
        title: "持ち家が向いている人の例",
        text: "",
      },
      {
        type: "list-good",
        items: [
          "同じエリアに長く住む予定がある。",
          "家族が固定されている（あるいは増える予定）。",
          "賃料が高く、購入のほうがコスト的に有利なエリアに住んでいる。",
          "インフレを資産で受け止めたい。",
        ],
      },
      {
        type: "callout-info",
        title: "賃貸が向いている人の例",
        text: "",
      },
      {
        type: "list-plain",
        items: [
          "転勤・転職の可能性がある。",
          "家族構成が変わる可能性がある。",
          "購入するエリアの将来性に不透明感がある。",
          "住宅ローンの返済よりも、手元資金の流動性を重視したい。",
        ],
      },
      {
        type: "paragraph",
        text: "どちらにも合理的な理由があります。「持ち家のほうが得」「賃貸のほうが自由」という単純な比較ではなく、自分のライフステージと照らし合わせて判断してください。",
      },
    ],
  },
  {
    id: "chapter-5",
    title: "第5章：住宅ローンは人生の自由度を固定する",
    content: [
      {
        type: "section-heading",
        text: "5.1 住宅ローンは「長期契約」であることを理解する",
      },
      {
        type: "paragraph",
        text: "住宅ローンは、多くの場合35年という長期契約です。",
      },
      {
        type: "paragraph",
        text: "35年間、毎月決まった金額を返済し続けるということは、その期間の収入・支出・ライフスタイルに制約がかかることを意味します。35年前を振り返れば、スマートフォンもなく、今と全く異なる社会でした。35年後も同様に、大きく変化した社会が待っています。",
      },
      {
        type: "paragraph",
        text: "その中で、毎月一定の返済を続けられるかどうか、十分に考えて借りることが必要です。",
      },
      {
        type: "section-heading",
        text: "5.2 変動金利と固定金利の使い分け",
      },
      {
        type: "paragraph",
        text: "住宅ローンの金利タイプは大きく「変動金利」と「固定金利」に分かれます。",
      },
      {
        type: "paragraph",
        text: "変動金利は、市場金利に連動して変動します。現在の低金利環境では返済額が抑えられますが、金利が上昇すると返済額も増加します。固定金利は、借入時の金利が返済期間中固定されます。返済額が変わらないため、家計計画が立てやすいです。",
      },
      {
        type: "paragraph",
        text: "2026年現在、日本銀行の金融政策の変化により、変動金利の上昇リスクが高まっています。変動金利を選択する場合は、金利が2〜3%上昇した場合でも返済可能かどうかを必ずシミュレーションしてください。",
      },
      {
        type: "section-heading",
        text: "5.3 借入可能額と返済可能額は違う",
      },
      {
        type: "paragraph",
        text: "金融機関が提示する「借入可能額」は、返済できる上限ではありません。",
      },
      {
        type: "paragraph",
        text: "一般的に、年収の7〜8倍程度まで借入可能とされていますが、これは返済の安全ラインではありません。実際に無理なく返済できる金額は、手取り収入の25%以内を目安に考えるべきです。",
      },
      {
        type: "paragraph",
        text: "借入可能額いっぱいまで借りることは、生活の自由度を大きく制約します。旅行・趣味・教育費・老後資金への積立など、住居費以外の支出が圧迫されます。",
      },
      {
        type: "section-heading",
        text: "5.4 ペアローンのリスク",
      },
      {
        type: "paragraph",
        text: "共働き夫婦が2人の収入を合算して借りる「ペアローン」は、借入可能額を増やせる反面、リスクがあります。",
      },
      {
        type: "paragraph",
        text: "一方の収入を前提としたギリギリの借入額にしている場合、片方の収入が止まると返済が困難になります。離婚した場合の物件・ローンの扱いも複雑になります。",
      },
      {
        type: "paragraph",
        text: "ペアローンを活用する場合は、一方の収入が減った想定でシミュレーションしてください。「今の収入が2人とも続く前提」で組んだローンは、人生のリスクに弱くなります。",
      },
      {
        type: "section-heading",
        text: "5.5 住宅ローンは返しながら豊かに暮らせるかで判断する",
      },
      {
        type: "paragraph",
        text: "住宅ローンを組む際の最終的な判断基準は、「返しながら豊かに暮らせるか」です。",
      },
      {
        type: "paragraph",
        text: "毎月の返済額だけでなく、固定資産税・修繕費・保険料・管理費まで含めた実質的な住居コストを計算してください。そこに生活費・教育費・老後資金を加えて、家計全体で成り立つかどうかを確認してください。",
      },
      {
        type: "paragraph",
        text: "住宅ローンは、借りることよりも、返し終えた後の人生を豊かにするために組むものです。返済中も豊かに暮らせることが、正しいローンの使い方です。",
      },
    ],
  },
  {
    id: "chapter-6",
    title: "第6章：買っていい物件・買ってはいけない物件",
    content: [
      {
        type: "section-heading",
        text: "6.1 物件を見る前に見るべき5つの数字",
      },
      {
        type: "paragraph",
        text: "物件のきれいさや間取りを見る前に、まず以下の5つの数字を確認してください。",
      },
      {
        type: "list-numbered",
        items: [
          "返済比率：手取り収入の25%以内かどうか。家計が耐えられるか確認する。",
          "金利+2%時の返済額：変動金利が2%上昇した場合に、返済を続けられるか確認する。",
          "管理費・修繕積立金（マンションの場合）：月々の実質コストを正確に把握する。",
          "周辺成約価格：相場より高値をつかんでいないか確認する。",
          "想定売却価格・想定賃料：出口戦略として、10〜15年後に売れるか・貸せるかを確認する。",
        ],
      },
      {
        type: "paragraph",
        text: "この5つを確認せずに「気に入った物件」を買うのは、出口なき投資です。不動産は、買う前の判断でほとんど勝負が決まります。",
      },
      {
        type: "section-heading",
        text: "6.2 危険な物件のサイン",
      },
      {
        type: "paragraph",
        text: "以下のサインが見られる物件は、慎重に判断してください。",
      },
      {
        type: "list-bad",
        items: [
          "修繕積立金が周辺相場より異常に安い：将来の値上げ・一時金リスクがあります。",
          "管理組合の議事録を出したがらない：管理上の問題を隠している可能性があります。",
          "ハザードリスクを営業担当が軽く扱う：保険料・資産価値・売却に影響します。",
          "駅から遠いのに価格が高い：将来の売却で苦戦しやすいです。",
          "周辺に同じような売り物件が多い：需給が弱いエリアのサインです。",
        ],
      },
      {
        type: "section-heading",
        text: "6.3 エリアの将来性を見極める3つの指標",
      },
      {
        type: "paragraph",
        text: "物件単体の評価に加えて、エリアの将来性を確認することが重要です。",
      },
      {
        type: "list-numbered",
        items: [
          "人口動態：そのエリアの人口は増えているか、減っているか。人口減少エリアは需要が下がり、売却・賃貸が難しくなります。",
          "再開発・インフラ整備計画：駅の新設・大規模再開発・道路整備は資産価値を押し上げます。逆に、廃線・工場閉鎖は価値を下げます。",
          "賃貸需要：そのエリアの空室率と賃料水準を確認してください。賃貸需要が強いエリアは、売れなくても貸せる選択肢があります。",
        ],
      },
      {
        type: "section-heading",
        text: "6.4 買っていい物件の条件",
      },
      {
        type: "paragraph",
        text: "以下の条件を満たす物件は、購入を検討する価値があります。",
      },
      {
        type: "list-good",
        items: [
          "返済比率が手取りの25%以内で収まる。",
          "金利2%上昇シナリオでも返済継続が可能。",
          "管理・修繕状態が良好で、修繕積立金が適正水準。",
          "駅から徒歩10分以内か、同等の利便性がある。",
          "ハザードリスクが許容範囲内。",
          "10〜15年後に売れる・貸せる根拠がある。",
        ],
      },
      {
        type: "paragraph",
        text: "これらの条件をすべて満たす物件は多くありません。しかし、これらを満たさない物件を買うことは、資産としてのリスクが高まります。",
      },
    ],
  },
  {
    id: "chapter-7",
    title: "第7章：インフレ時代の最終結論＋資料",
    content: [
      {
        type: "section-heading",
        text: "7.1 焦って買うことのリスク",
      },
      {
        type: "paragraph",
        text: "住宅購入の最大の失敗パターンの一つが、「焦り」による購入です。",
      },
      {
        type: "paragraph",
        text: "「今が最後のチャンス」「金利が上がる前に」「この物件を逃したら後悔する」。こうした言葉は、営業担当者が使いやすいフレーズです。しかし、焦って購入した住宅は、後悔につながるケースが多いです。",
      },
      {
        type: "paragraph",
        text: "住宅購入は、一生に一度あるかないかの大きな意思決定です。焦りで判断するのではなく、自分のライフステージと財務状況を冷静に確認してから決断してください。",
      },
      {
        type: "section-heading",
        text: "7.2 でも先送りしすぎるリスクもある",
      },
      {
        type: "paragraph",
        text: "一方で、ずっと先送りし続けることにもリスクがあります。",
      },
      {
        type: "paragraph",
        text: "年齢が上がると、住宅ローンの借入可能期間が短くなります。例えば、65歳完済を前提とすると、40歳では25年、45歳では20年しか借りられません。借入期間が短くなると、毎月の返済額が増え、家計への負担が大きくなります。",
      },
      {
        type: "paragraph",
        text: "また、インフレが続く環境では、購入を先送りするほど、取得コストが上がる可能性があります。「もっと安くなるまで待つ」という判断が、結果として高値つかみになるリスクもあります。",
      },
      {
        type: "section-heading",
        text: "7.3 住宅購入の意思決定フレームワーク",
      },
      {
        type: "paragraph",
        text: "住宅購入を検討する際は、以下のフレームワークで判断してください。",
      },
      {
        type: "list-numbered",
        items: [
          "財務的準備：自己資金は物件価格の10〜20%以上あるか。返済比率は手取りの25%以内か。生活防衛資金（生活費6か月分）は手元に残るか。",
          "ライフステージの確認：今後5〜10年、同じエリアに住み続ける可能性が高いか。家族構成の変化（結婚・出産・子どもの独立）は概ね見通せるか。",
          "物件の精査：6章の「買っていい物件の条件」を満たしているか。エリアの将来性に問題はないか。",
          "感情的な確認：焦りや外部からのプレッシャーで判断していないか。家族全員が納得して購入する意思を持っているか。",
        ],
      },
      {
        type: "paragraph",
        text: "この4つを確認してから決断することで、後悔のない住宅購入に近づきます。",
      },
      {
        type: "section-heading",
        text: "7.4 条件が整ったら動く",
      },
      {
        type: "paragraph",
        text: "住宅購入前に、以下を確認してください。",
      },
      {
        type: "checklist-group",
        title: "財務確認",
        items: [
          "返済比率25%以内",
          "自己資金10〜20%以上",
          "生活防衛資金確保",
          "金利2%上昇シミュレーション実施済み",
        ],
      },
      {
        type: "checklist-group",
        title: "物件確認",
        items: [
          "ハザードマップ確認",
          "修繕積立金適正水準",
          "管理組合・議事録確認",
          "ホームインスペクション実施",
          "周辺成約価格確認",
        ],
      },
      {
        type: "checklist-group",
        title: "エリア確認",
        items: [
          "人口動態確認",
          "将来の再開発・インフラ計画確認",
          "賃貸需要・空室率確認",
        ],
      },
      {
        type: "checklist-group",
        title: "意思決定",
        items: [
          "焦りではなく合理的判断",
          "家族全員の合意",
          "出口戦略（売却・賃貸）の想定",
        ],
      },
    ],
  },
];

export const authorNote = [
  "この資料を最後まで読んでいただき、ありがとうございます。",
  "住宅購入は、金額の大きさゆえに慎重に考えるべき意思決定です。しかし、慎重になりすぎて何も動けなくなるのも問題です。",
  "重要なのは、「感情で動かない」ことです。焦り・憧れ・周囲へのプレッシャーではなく、自分と家族の状況を冷静に分析した上で判断してください。",
  "この資料が、あなたの住宅購入の意思決定に少しでも役立てば幸いです。",
  "最後になりますが、私の著書である「あなたをインフレ時代の勝者にする」で記載できなかった部分をこちらの住宅購入戦略に記載致しました。合わせてお読みいただけると幸いです。",
];

export const consultationSection = {
  title: "ご相談",
  body: "住宅購入で迷ったら、一度相談してください。住宅購入は、物件選びだけでなく、家計・ローン・税制・将来の出口まで含めた総合判断です。ご自身の状況で、「今買うべきか」「どの物件なら買ってよいか」「借入額はいくらまでが安全か」を確認したい方は、LINEまたは会社の問い合わせフォームよりご連絡ください。",
  lineUrl: "https://line.me/ti/p/@tes2030",
};

export const disclaimer =
  "本資料は情報提供を目的としたものであり、特定の物件・金融商品・投資行動を推奨するものではありません。不動産購入は個人の状況により判断が異なります。専門家（不動産会社・ファイナンシャルプランナー・税理士等）への相談を推奨します。本資料の情報基準日は2026年4月時点です。";